<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'
import { hasProductAccess } from '@/Utils/teamPermissionRows'
import { showModal } from '@/Utils/modalBus'
import { useSiteDraft } from '@/composables/useSiteDraft'
import { useSiteHostReadiness } from '@/composables/useSiteHostReadiness'
import { useSiteGiving } from '@/composables/useSiteGiving'
import { siteTemplate, isBroadcast as isBroadcastSite, templateMeta } from '@/components/Sites/siteTemplates'
import { buildSiteNav, findNavEntry, routeTemplateMismatch } from './siteNav'
import Spinner from '@/components/ui/Spinner.vue'
import NoAccessState from '@/components/ui/NoAccessState.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import TabGroup from '@/components/Atoms/TabGroup.vue'
import TabItem from '@/components/Atoms/TabItem.vue'
import IconBase from '@/components/icon/IconBase.vue'
import BackButton from '@/components/Atoms/BackButton.vue'
import IconLinkExternal from '@/components/icon/IconLinkExternal.vue'
import IconEyeOutline from '@/components/icon/IconEyeOutline.vue'
import IconLock from '@/components/icon/IconLock.vue'
import SiteToasts from '@/components/Sites/SiteToasts.vue'
import SiteHostStatusPill from '@/components/Sites/SiteHostStatusPill.vue'
import SiteAddressSetupNotice from '@/components/Sites/SiteAddressSetupNotice.vue'

// Shell for one site: header (name, status chip, Preview / Publish / Open
// site), the "N unpublished changes" bar and the grouped navigation — a left
// rail from `lg` up, group tabs + sub-tabs below. Each section is a nested
// route that receives the site as a prop and talks to castr's /sites/:id/*.
//
// Right after creation the address is not reachable yet (App Platform routes
// a new alias in 1–2 min); the shell polls castr's readiness and keeps
// Preview / Open site off until the address serves (Trello oP0vr33o).
const props = defineProps({
	siteId: {
		type: String,
		required: true,
	},
})

const store = useStore()
const route = useRoute()
const router = useRouter()
const { notify } = useNotification()
const draft = useSiteDraft(() => props.siteId)
const readiness = useSiteHostReadiness(() => props.siteId, { poll: true })
const giving = useSiteGiving(() => props.siteId)

const storedSite = (id) => store.getters['Sites/siteById'](id)
const loading = ref(!storedSite(props.siteId))
const notFound = ref(false)
let resolveGeneration = 0

const activeTeam = computed(() => store.state.User.activatedTeam)
const userPermissions = computed(() => store.getters['User/userPermissions'])
const canAccessSites = computed(() => !activeTeam.value || hasProductAccess(userPermissions.value, 'sites'))
const ownerEmail = computed(() => store.getters['User/teamOwner']?.email || '')
const site = computed(() => store.getters['Sites/siteById'](props.siteId))
const status = computed(() => site.value?.status)
const isLive = computed(() => status.value === 'active')
const isDraftSite = computed(() => status.value === 'draft')
// `creating` = legacy pre-draft site whose first publish failed; treat as unpublished.
const isUnpublished = computed(() => isDraftSite.value || status.value === 'creating')
// Address still being routed by the edge: nothing that opens the site works yet.
const addressPending = computed(() => readiness.gated.value)
const lockedTitle = readiness.lockedTitle

// --- navigation ---------------------------------------------------------------
// The home-page template (castr `site.template`) decides which pages are
// offered: broadcast drops Categories / Featured and adds Settings › Broadcast
// plus, with native giving, Monetize › Donations. Deep links to pages the
// template does not use still render, under a one-line notice.
const template = computed(() => siteTemplate(site.value))
const isBroadcast = computed(() => isBroadcastSite(site.value))
const customDomainLocked = computed(() => store.getters['Sites/customDomainLocked'])
const nav = computed(() => buildSiteNav({ template: template.value, givingMode: isBroadcast.value ? giving.mode.value : 'off', customDomainLocked: customDomainLocked.value }))
const LOCKED_NAV_TITLE = 'Not included in your current plan'
const activeEntry = computed(() => {
	const shown = findNavEntry(route.name, nav.value)
	return shown.group ? shown : findNavEntry(route.name)
})
const activeGroup = computed(() => activeEntry.value.group)
const templateMismatch = computed(() => routeTemplateMismatch(route.name, template.value))
const templateNotice = computed(() => {
	if (templateMismatch.value === 'hidden') return `Not used by the ${templateMeta(template.value).name} template — your site's home page does not show this.`
	if (templateMismatch.value === 'broadcast-only') return `Only used by the ${templateMeta('broadcast').name} template — switch templates under Settings › General to use this.`
	return ''
})
// Giving mode drives the Donations entry: load it for broadcast sites only.
watch(isBroadcast, (on) => { if (on) giving.load() }, { immediate: true })
const isRouteActive = (name) => route.name === name
const groupTarget = (group) => ({ name: group.route || group.children[0].route, params: { siteId: props.siteId } })
const childTarget = (child) => ({ name: child.route, params: { siteId: props.siteId } })

// --- draft / publish -----------------------------------------------------------
const hasDraft = computed(() => draft.hasDraft.value)
const draftCount = computed(() => draft.draftCount.value)
const publishing = computed(() => draft.state.value.publishing)
const previewing = computed(() => draft.state.value.previewing)
const discarding = computed(() => draft.state.value.discarding)
const showPublish = computed(() => isUnpublished.value || hasDraft.value)
const publishLabel = computed(() => (isUnpublished.value ? 'Publish site' : 'Publish changes'))

const publish = () => draft.publish().then(() => null, () => null)
const openPreview = () => {
	if (addressPending.value) return
	draft.openPreview()
}
const requestDiscard = () => showModal('discard-site-draft')
const confirmDiscard = () => draft.discard().then(() => null, () => null)

async function resolveSite() {
	const requestedId = props.siteId
	const generation = ++resolveGeneration
	const seeded = !!storedSite(requestedId)
	notFound.value = false
	if (seeded) {
		loading.value = false
		draft.refresh()
	} else {
		loading.value = true
	}
	try {
		await store.dispatch('Sites/getSite', requestedId)
		if (generation !== resolveGeneration || props.siteId !== requestedId) return
	} catch (err) {
		if (generation !== resolveGeneration || props.siteId !== requestedId) return
		if (err?.code === 404) {
			notFound.value = true
		} else if (String(err?.message || err).includes('not have access to')) {
			store.dispatch('Sites/setTeamPermissionError', { error: true })
		} else {
			notify({ group: 'error', text: err.message || 'Could not load the site.' })
		}
	}
	if (generation !== resolveGeneration || props.siteId !== requestedId) return
	loading.value = false
	if (!seeded && site.value && !notFound.value) draft.refresh()
	if (!store.getters['Sites/capsLoaded']) store.dispatch('Sites/loadCaps').catch(() => null)
}

onMounted(() => { resolveSite() })

watch(() => props.siteId, (next, prev) => {
	if (next && next !== prev) resolveSite()
})

// castr's Site carries a best-effort `draftCount` (null = unknown); seed the
// indicator from it until the runtime answer arrives.
watch(() => site.value?.draftCount, (n) => {
	if (typeof n === 'number' && !draft.state.value.loaded) draft.state.value.draftCount = n
}, { immediate: true })

const openSite = () => {
	if (addressPending.value) return
	if (site.value?.url) window.open(site.value.url, '_blank', 'noopener')
}
</script>

<template>
	<div class="w-full container detail-shell px-4 mt-4 lg:pt-8 lg:self-start">
		<div v-if="loading" class="flex items-center justify-center min-h-[70vh]" aria-busy="true">
			<Spinner classes="text-dark-8" spinner-color="var(--c-dark-8)" spinner-size="22px" />
		</div>

		<main v-else-if="!canAccessSites">
			<NoAccessState :owner-email="ownerEmail" />
		</main>

		<main v-else-if="notFound || !site" class="flex flex-col items-center justify-center min-h-[60vh] text-center">
			<h2 class="text-headline font-semibold text-surface-text-2 mb-2">Site not found</h2>
			<p class="text-tiny text-surface-8 mb-6">It may have been deleted, or it belongs to another account.</p>
			<FormButton type="secondary" :to="{ name: 'SitesDashboard' }">Back to Sites</FormButton>
		</main>

		<template v-else>
			<header class="flex flex-wrap items-center gap-x-4 gap-y-3 mb-4 px-4 lg:px-0">
				<BackButton :to="{ name: 'SitesDashboard' }" />
				<div class="min-w-0 flex-1">
					<h1 class="m-0 flex items-center gap-2 text-lg font-semibold text-white">
						<span class="truncate">{{ site.name }}</span>
						<SiteHostStatusPill :site="site" />
					</h1>
					<p class="mb-0 truncate text-tiny text-surface-7" data-testid="site-header-host">
						{{ site.canonicalHost || site.primaryHost }}<span v-if="addressPending" class="text-surface-6"> · not reachable yet</span>
					</p>
				</div>
				<div class="flex shrink-0 items-center gap-2" data-testid="site-header-actions">
					<FormButton type="secondary" size="md" class="gap-1.5" :is-loading="previewing" :disabled="previewing || addressPending"
						:title="addressPending ? lockedTitle : 'Open a preview with your unpublished changes'" data-testid="site-preview" @click="openPreview">
						<icon-base :width="16" :height="16" :width-view-box="24" :height-view-box="24" fill="none">
							<icon-eye-outline />
						</icon-base>
						<span class="hidden sm:inline">Preview</span>
					</FormButton>
					<FormButton v-if="showPublish" type="main" size="md" :is-loading="publishing" :disabled="publishing" data-testid="site-publish" @click="publish">
						{{ publishLabel }}
					</FormButton>
					<FormButton v-else type="secondary" size="md" class="gap-1.5" :disabled="!isLive || addressPending" :title="addressPending ? lockedTitle : 'Open the live site'" data-testid="site-open" @click="openSite">
						<icon-base :width="16" :height="16" fill="none">
							<icon-link-external />
						</icon-base>
						<span class="hidden sm:inline">Open site</span>
					</FormButton>
				</div>
			</header>

			<!-- Address still being set up by the edge: Preview / Open site wait for it -->
			<SiteAddressSetupNotice :site-id="siteId" class="mb-4 mx-4 lg:mx-0" />

			<!-- Unpublished settings changes (runtime draft) -->
			<div v-if="hasDraft" class="mb-4 mx-4 lg:mx-0 flex flex-wrap items-center gap-3 rounded-lg border border-surface-4 bg-surface-2 px-4 py-2.5" data-testid="site-draft-bar" role="status">
				<span class="inline-flex items-center gap-2 text-sm text-white">
					<span class="size-2 rounded-full bg-gold" aria-hidden="true"></span>
					{{ draftCount }} unpublished {{ draftCount === 1 ? 'change' : 'changes' }}
				</span>
				<span class="hidden text-tiny text-surface-7 sm:inline">Viewers still see the last published version.</span>
				<div class="ml-auto flex items-center gap-2">
					<FormButton type="link" size="sm" :disabled="discarding" data-testid="site-discard-draft" @click="requestDiscard">Discard</FormButton>
					<FormButton type="secondary" size="sm" :is-loading="previewing" :disabled="previewing || addressPending" :title="addressPending ? lockedTitle : undefined" data-testid="site-draft-preview" @click="openPreview">Preview</FormButton>
					<FormButton type="main" size="sm" :is-loading="publishing" :disabled="publishing" data-testid="site-publish-changes" @click="publish">Publish changes</FormButton>
				</div>
			</div>

			<div class="lg:grid lg:grid-cols-[11.5rem_minmax(0,1fr)] lg:items-start lg:gap-6">
				<nav aria-label="Site sections" class="mb-5 px-4 lg:px-0 lg:mb-0 lg:sticky lg:top-6" data-testid="site-nav" :data-template="template">
					<!-- ≥ lg: left rail -->
					<ul class="hidden lg:flex m-0 list-none flex-col gap-1 p-0">
						<li v-for="group in nav" :key="group.id" :data-testid="`nav-group-${group.id}`">
							<router-link v-if="!group.children" :to="groupTarget(group)"
								class="flex h-8 items-center rounded-md px-2.5 text-sm font-medium transition-colors hover:no-underline"
								:class="activeGroup?.id === group.id ? 'bg-surface-4 text-white' : 'text-surface-8 hover:bg-surface-3 hover:text-white'"
								:aria-current="activeGroup?.id === group.id ? 'page' : undefined" :data-testid="`nav-${group.route}`">
								{{ group.label }}
							</router-link>
							<template v-else>
								<div class="mt-3 mb-1 px-2.5 text-[0.65rem] font-semibold uppercase tracking-wider text-surface-6">{{ group.label }}</div>
								<ul class="m-0 list-none flex flex-col gap-0.5 p-0">
									<li v-for="child in group.children" :key="child.route">
										<router-link :to="childTarget(child)"
											class="flex h-8 items-center rounded-md px-2.5 text-sm transition-colors hover:no-underline"
											:class="isRouteActive(child.route) ? 'bg-surface-4 font-medium text-white' : 'text-surface-8 hover:bg-surface-3 hover:text-white'"
											:aria-current="isRouteActive(child.route) ? 'page' : undefined" :data-testid="`nav-${child.route}`"
											:title="child.locked ? LOCKED_NAV_TITLE : undefined">
											{{ child.label }}
											<icon-base v-if="child.locked" class="ml-auto text-surface-6" :width="12" :height="12" fill="none" :data-testid="`nav-lock-${child.route}`"><icon-lock /></icon-base>
										</router-link>
									</li>
								</ul>
							</template>
						</li>
					</ul>

					<!-- < lg: group tabs, then the active group's sub-tabs -->
					<div class="lg:hidden flex flex-col gap-2">
						<TabGroup type="segmented" custom-classes="overflow-x-auto no-scrollbar">
							<TabItem v-for="group in nav" :key="group.id" :is-active="activeGroup?.id === group.id" :data-testid="`tab-group-${group.id}`"
								@click="router.push(groupTarget(group))">
								{{ group.label }}
							</TabItem>
						</TabGroup>
						<TabGroup v-if="activeGroup?.children" type="segmented" size="sm" custom-classes="overflow-x-auto no-scrollbar" data-testid="site-subnav">
							<TabItem v-for="child in activeGroup.children" :key="child.route" :is-active="isRouteActive(child.route)" :data-testid="`tab-${child.route}`"
								:title="child.locked ? LOCKED_NAV_TITLE : undefined" @click="router.push(childTarget(child))">
								{{ child.label }}
								<icon-base v-if="child.locked" class="ml-1 text-surface-6" :width="11" :height="11" fill="none"><icon-lock /></icon-base>
							</TabItem>
						</TabGroup>
					</div>
				</nav>

				<div class="min-w-0">
					<!-- Deep link to a page the current template does not use -->
					<p v-if="templateNotice" class="mb-4 flex flex-wrap items-center gap-x-2 rounded-lg border border-surface-4 bg-surface-2 px-4 py-2.5 text-tiny text-surface-8" role="note" data-testid="site-template-notice" :data-reason="templateMismatch">
						<span>{{ templateNotice }}</span>
						<router-link :to="{ name: 'SiteGeneral', params: { siteId } }" class="text-main-light underline-offset-2 hover:underline">Templates</router-link>
					</p>
					<router-view :site="site" :site-id="siteId" />
				</div>
			</div>

			<SiteToasts />
			<ConfirmModal modal-id="discard-site-draft" modal-type="danger" ok-text="Discard changes" cancel-text="Keep editing"
				title="Discard unpublished changes?"
				:message="`${draftCount} unpublished ${draftCount === 1 ? 'change is' : 'changes are'} thrown away and the settings go back to what viewers see today.`"
				@modal-confirm="confirmDiscard" />
		</template>
	</div>
</template>
